import { HybridCloudService } from '@qdrant/qdrant-cloud-public-api/hybrid/v1';
import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { isForbiddenError, isNotFoundError } from '@/api/errors';
import { HybridCloudServices } from '@/api/services/hybrid-cloud';
import { getHybridCloudEnvironmentStage } from '@/components/HybridCloudEnvironments/utils';
import { head } from '@/router/Head';
import { resolveAccountHasRpcPermissions } from '@/router/resolvers/user';
import { validateParams } from '@/router/utils';

export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/hybrid-cloud/$hybridCloudEnvId')({
  params: validateParams(
    z.object({
      hybridCloudEnvId: z.guid(),
    }),
  ),
  beforeLoad: async ({ context: { queryClient }, params: { accountId, hybridCloudEnvId } }) => {
    const hybridCloudEnvQuery = HybridCloudServices.getHybridCloudEnvironmentQuery({
      accountId,
      hybridCloudEnvironmentId: hybridCloudEnvId,
    });

    try {
      const { hybridCloudEnvironment } = await queryClient.fetchQuery(hybridCloudEnvQuery);
      const hasWritePermission = await resolveAccountHasRpcPermissions(
        [HybridCloudService.method.updateHybridCloudEnvironment],
        { queryClient, accountId },
      );
      const stage = getHybridCloudEnvironmentStage(hybridCloudEnvironment);
      //  When user doesn't have write permissions and the env hasn't finished configuring yet, redirect to the list.
      if (stage !== 'ready' && !hasWritePermission) {
        throw redirect({
          to: '/accounts/$accountId/hybrid-cloud',
          params: { accountId },
          replace: true,
        });
      }
      const onboardingPathPrefix = `/hybrid-cloud/${hybridCloudEnvId}/onboarding`;
      const isConnectionStep = location.pathname.includes(`${onboardingPathPrefix}/connection`);

      if (stage === 'connection' && !isConnectionStep) {
        throw redirect({
          to: '/accounts/$accountId/hybrid-cloud/$hybridCloudEnvId/onboarding/connection',
          params: { accountId, hybridCloudEnvId },
        });
      }

      // Prevent access to the connection step if the environment is past the connection stage.
      if (stage === 'ready' && isConnectionStep) {
        throw redirect({
          to: '/accounts/$accountId/hybrid-cloud/$hybridCloudEnvId/overview',
          params: { accountId, hybridCloudEnvId },
          replace: true,
        });
      }

      // The root hybrid cloud environment route has no content of its own, redirect to the overview tab.
      if (location.pathname.endsWith(hybridCloudEnvId)) {
        throw redirect({
          to: '/accounts/$accountId/hybrid-cloud/$hybridCloudEnvId/overview',
          params: { accountId, hybridCloudEnvId },
          replace: true,
        });
      }
    } catch (error) {
      if (isForbiddenError(error) || isNotFoundError(error)) {
        throw redirect({
          to: '/accounts/$accountId/hybrid-cloud',
          params: { accountId },
          replace: true,
        });
      }
      throw error;
    }

    return { hybridCloudEnvQuery };
  },
  head,
  staticData: {
    title: 'Hybrid Cloud Environment',
  },
  component: lazyRouteComponent(() => import('./route.component'), 'HybridCloudEnvIdComponent'),
  // `beforeLoad` awaits the environment to pick the stage redirects, so the page's skeleton stands in.
  pendingMs: 200,
  pendingComponent: lazyRouteComponent(() => import('./route.component'), 'HybridCloudEnvIdPendingComponent'),
});
